import { Component } from 'react'
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import {
  Shield, Trophy, Clock, ChevronRight,
  MapPin, Smartphone, Users, Flag, Home, Check,
} from 'lucide-react'

import AdminApp from './admin/AdminApp.jsx'
import AIAssistant from './components/AIAssistant.jsx'
import Concierge from './components/Concierge.jsx'
import QualifyFollow from './components/QualifyFollow.jsx'
import LiveRedirect from './live/LiveRedirect.jsx'
import CalculatorApp from './calculator/CalculatorApp.jsx'
import ScoreApp from './score/ScoreApp.jsx'
import ScorecardMockup from './score/ScorecardMockup.jsx'
import LeaderboardApp from './leaderboard/LeaderboardApp.jsx'
import { TvScreen, TvRotator, TvScreenByPath } from './leaderboard/TvHub.jsx'
import RegisterApp from './register/RegisterApp.jsx'
import QualifyApp from './qualify/QualifyApp.jsx'
import PlayApp from './play/PlayApp.jsx'
import DiningApp from './dining/DiningApp.jsx'
import CalcuttaApp from './calcutta/CalcuttaApp.jsx'
import CalcuttaBidApp from './calcutta/CalcuttaBidApp.jsx'
import CalcuttaLive from './calcutta/CalcuttaLive.jsx'
import CalcuttaRemote from './calcutta/CalcuttaRemote.jsx'
import HubApp from './hub/HubApp.jsx'
import PhotosApp from './photos/PhotosApp.jsx'
import PhotoWallApp from './photos/PhotoWallApp.jsx'
import PhotoBrowse from './photos/PhotoBrowse.jsx'
import PhotoView from './photos/PhotoView.jsx'
import HistoryApp from './history/HistoryApp.jsx'
import ResultsBoard from './results/ResultsBoard.jsx'
import EventTitle from './components/EventTitle.jsx'
import TvControlApp from './tvcontrol/TvControlApp.jsx'
import TestStationApp from './teststation/TestStationApp.jsx'
import GpsInject from './teststation/GpsInject.jsx'
import TestLeaderboard from './teststation/TestLeaderboard.jsx'
import TestPhoto from './teststation/TestPhoto.jsx'
import TestPhotoWall from './teststation/TestPhotoWall.jsx'
import TrialGate, { trialSignOut } from './auth/TrialGate.jsx'
import DemoAdminBanner from './admin/DemoAdminBanner.jsx'
import BoardAutoUpdater from './lib/autoUpdate.jsx'
import OfflineBanner from './components/OfflineBanner.jsx'
import useWakeLock from './lib/useWakeLock.js'
import {
  PbccFeatureBar,
  PbccHeroHeader,
  PbccRulesCard,
} from './components/PbccLayout.jsx'
import CLUB from './config/club.js'
import { FOURBALL_ONLY, PBCC_PREMIUM } from './config/siteMode.js'

// 4-Ball site: NO gold. Gold accents become white on DARK surfaces and green on
// WHITE surfaces (so nothing goes invisible). Full-featured builds keep gold.
const LAND_GOLD_ON_DARK = FOURBALL_ONLY ? '#FFFFFF' : CLUB.accentColor
const LAND_GOLD_ON_LIGHT = FOURBALL_ONLY ? '#123D26' : CLUB.accentColor
// Bottom section (feature bar + body) — much darker green on the 4-Ball site,
// near the deep green at the base of the hero. Full builds keep the mid green.
const LAND_BODY_GREEN = FOURBALL_ONLY ? '#123D26' : '#123D26'

export default function App() {
  // Keep the iPhone screen on everywhere in the app — scorecards, GPS map,
  // qualifying forms, leaderboards. Requests navigator.wakeLock on load and
  // reacquires it on visibilitychange (the API drops the lock on tab swap).
  // Hoisted to the top-level App so every route inherits it.
  useWakeLock(true)
  // Host-based landing: opened via the live.* short domain, land straight on
  // the follow-live hub (/live → resolves the current event → /hub). The main
  // domain keeps the normal Landing.
  const isLiveHost = typeof window !== 'undefined' && window.location.hostname.startsWith('live.')
  return (
    <ErrorBoundary>
      <TrialGate>
        {/* No-op on the PBCC build (no /config/demoBanner in that DB).
            Lights up only on the white-label demo deploy. */}
        <DemoAdminBanner />
        <OfflineBanner />
        <Routes>
          {/* TEST: there is NO public marketing landing on this 4Ball-only site.
              "/" goes straight to /admin, which runs the access-code → dashboard
              flow. <Landing> never renders here. Club/live keep the landing. */}
          <Route path="/" element={
            PBCC_PREMIUM
              ? <Navigate to="/admin/pbcc-4ball" replace />
              : (isLiveHost ? <Navigate to="/live" replace /> : <Landing />)
          } />
          <Route path="/live" element={<LiveRedirect />} />
          {!FOURBALL_ONLY && <Route path="/calculator" element={<CalculatorApp />} />}
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="/score" element={<ScoreApp />} />
          <Route path="/score-mockup" element={<ScorecardMockup />} />
          <Route path="/leaderboard" element={<LeaderboardApp />} />
          <Route path="/tv" element={<TvRotator />} />
          <Route path="/tv/scores" element={<TvScreen kind="scores" />} />
          {/* Retired: the full-screen twin. /tv/scrollmap is the ONE map. */}
          <Route path="/tv/map" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/tv/photos" element={<TvScreen kind="photos" />} />
          <Route path="/tv/split" element={<TvScreen kind="split" />} />
          <Route path="/tv/brackets" element={<TvScreen kind="brackets" />} />
          <Route path="/tv/scrollmap" element={<TvScreen kind="scrollmap" />} />
          <Route path="/tv/matchboard" element={<TvScreen kind="matchboard" />} />
          {/* Short alias: /tv/1 = screen 1, /tv/1c = Calcutta screen 1. The fixed
              /tv/{kind} routes above win via static-over-dynamic ranking. */}
          <Route path="/tv/:screen" element={<TvScreenByPath />} />
          <Route path="/tvcontrol" element={<TvControlApp />} />
          <Route path="/register" element={<RegisterApp />} />
          <Route path="/qualify" element={<QualifyApp />} />
          <Route path="/play" element={<PlayApp />} />
          <Route path="/dining" element={<DiningApp />} />
          <Route path="/calcutta" element={<CalcuttaApp />} />
          <Route path="/calcutta-bid" element={<CalcuttaBidApp />} />
          <Route path="/calcutta-live" element={<CalcuttaLive />} />
          <Route path="/calcutta-remote" element={<CalcuttaRemote />} />
          <Route path="/hub" element={<HubApp />} />
          <Route path="/photos" element={<PhotosApp />} />
          <Route path="/photowall" element={<PhotoBrowse />} />
          <Route path="/photo" element={<PhotoView />} />
          <Route path="/slideshow" element={<PhotoWallApp />} />
          <Route path="/history" element={<HistoryApp />} />
          <Route path="/results" element={<ResultsBoard />} />
          {/* Retired maps/GPS-test views — /tv/scrollmap is the ONE and ONLY map. */}
          <Route path="/map" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/gpstest" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/gpstestwatch" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/gpslivetest" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/teststation" element={<TestStationApp />} />
          <Route path="/test" element={<TestStationApp />} />
          {/* TEST-only GPS injector — writes tracking to the standalone test DB. */}
          <Route path="/gpsinject" element={<GpsInject />} />
          <Route path="/cartcalibrate" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/testleaderboard" element={<TestLeaderboard />} />
          <Route path="/testphoto" element={<TestPhoto />} />
          <Route path="/testphotowall" element={<TestPhotoWall />} />
          {/* Retired calibration SANDBOXES — spot placement now lives on
              /tv/scrollmap's "Place spots". All redirect to the ONE map. */}
          <Route path="/teststation/map2" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/teststation/map2/capture" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/teststation/map2/broadcast" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/teststation/home" element={<Navigate to="/tv/scrollmap" replace />} />
          <Route path="/teststation/home/broadcast" element={<Navigate to="/tv/scrollmap" replace />} />
          {/* Short TV-friendly aliases for typing into TV browsers on the short
              domain (pgt.web.app live / pbtv.web.app test). /1…/100 → the numbered
              TV screens (/tv?screen=N); /a → admin; /c → TV Control. Explicit
              per-number routes so each outranks the catch-all with no ambiguity. */}
          {PBCC_PREMIUM && Array.from({ length: 100 }, (_, i) => i + 1).map((n) => (
            <Route key={`tv-${n}`} path={`/${n}`} element={<Navigate to={`/tv?screen=${n}`} replace />} />
          ))}
          {PBCC_PREMIUM && <Route path="/a" element={<Navigate to="/admin" replace />} />}
          {PBCC_PREMIUM && <Route path="/c" element={<Navigate to="/tvcontrol" replace />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <GlobalAIAssistant />
        {/* Read-only tournament concierge for players/spectators — separate from
            the admin edit AI above. Player/spectator phone screens only. */}
        <PlayerConcierge />
        {/* Persistent "Start Qualifying" pill that follows a pending team across
            the full app (hub/leaderboard/photos/etc.). Hidden on /play (the team
            home has the two choice buttons) and /qualify (already qualifying). */}
        <QualifyFollowGate />
        {/* Auto-reload TV/board pages when a newer build is deployed. */}
        <BoardAutoUpdater />
      </TrialGate>
    </ErrorBoundary>
  )
}

// The floating AI assistant is an OPERATOR tool, so it renders only on
// operator-facing screens — never on player/spectator surfaces (landing,
// /register, /qualify, /calcutta*, /hub, /photos, /photowall, /history) or
// the /tv/* display boards. Admin routes (/admin/*) mount their own
// context-aware AIAssistant (tournamentId / demo mode), so they are handled
// there, not here. This is an allowlist: anything not listed gets no circle.
const AI_OPERATOR_PATHS = [
  '/map', '/tvcontrol',
  '/gpstest', '/gpstestwatch', '/gpslivetest',
  '/teststation', '/test', '/testleaderboard', '/testphoto', '/testphotowall',
]
function GlobalAIAssistant() {
  const { pathname } = useLocation()
  const show = AI_OPERATOR_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
  if (!show) return null
  return <AIAssistant tournamentId={null} />
}

// The READ-ONLY tournament concierge for players & spectators. Shows on the
// phone player/spectator surfaces only. Completely separate from the admin edit
// AI — it can only look things up from live 4-Ball data, never change anything.
const CONCIERGE_PLAYER_PATHS = ['/play', '/qualify', '/hub', '/live', '/leaderboard']
function PlayerConcierge() {
  const { pathname } = useLocation()
  const show = CONCIERGE_PLAYER_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
  if (!show) return null
  // On the hub, drop the Ask AI button down beside the tall tree logo — out of
  // the top-right corner where the (now-bigger) Photos pill sits.
  const onHub = pathname === '/hub' || pathname.startsWith('/hub/')
  // Spectator surfaces (hub/live/leaderboard) hide the Rules + My Match chips —
  // those are player/companion-app features. Player web (/play, /qualify) keeps them.
  const SPECTATOR_PATHS = ['/hub', '/live', '/leaderboard']
  const spectator = SPECTATOR_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
  return <Concierge spectator={spectator} fabTop={onHub ? 'calc(100px + env(safe-area-inset-top))' : undefined} />
}

// The persistent "Start Qualifying" pill follows a pending team across the full
// app's browse surfaces — but NOT on /play (its team home already has the two
// choice buttons) or /qualify (they're already in the qualify flow).
const QUALIFY_FOLLOW_PATHS = ['/hub', '/live', '/leaderboard', '/photos', '/photowall', '/photo', '/slideshow', '/history', '/calcutta']
function QualifyFollowGate() {
  const { pathname } = useLocation()
  const show = QUALIFY_FOLLOW_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
  if (!show) return null
  return <QualifyFollow />
}

// Catches any render-time exception bubbling out of a route component and
// shows a useful message instead of a blank screen. Especially helpful for
// new screens like /map where I don't have the user's console handy.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, componentStack: null }
  }
  static getDerivedStateFromError(error) {
    return { error }
  }
  componentDidCatch(error, info) {
    // Capture the component stack so it can be shown ON-SCREEN — the boards run
    // on TVs with no dev console, and the component stack pinpoints exactly
    // which component threw.
    this.setState({ componentStack: info?.componentStack || null })
    // eslint-disable-next-line no-console
    console.error('[CompleteTourneyLive] render crash:', error, info)
  }
  componentDidUpdate(_, prevState) {
    if (!(this.state.error && !prevState.error && typeof window !== 'undefined')) return
    const path = window.location.pathname || '/'
    // ONLY a deep /admin page crash bounces to the admin home. Public / board /
    // TV routes NEVER redirect — they render the error + component stack
    // on-screen so it can be read directly off the board or TV.
    if (path.startsWith('/admin/') || path.startsWith('/admin?')) {
      this._redirectTimer = setTimeout(() => {
        this.setState({ error: null })
        window.location.href = '/admin'
      }, 1800)
    }
  }

  componentWillUnmount() {
    if (this._redirectTimer) clearTimeout(this._redirectTimer)
  }

  render() {
    if (this.state.error) {
      const path = (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '')
      const pathname = (typeof window !== 'undefined' ? window.location.pathname : '')
      const isAdminSubpage = pathname.startsWith('/admin/') || pathname.startsWith('/admin?')
      const err = this.state.error
      const message = String(err?.message || err)
      const errStack = String(err?.stack || '')
      const compStack = String(this.state.componentStack || '')
      const pre = {
        fontSize: 13, lineHeight: 1.5, background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10, padding: 12, marginTop: 4,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'auto',
      }
      const lbl = { fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.55)', marginTop: 16 }
      return (
        <div style={{ position: 'fixed', inset: 0, overflow: 'auto', backgroundColor: '#123D26', color: '#FFFFFF', padding: 20, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#FFD37A' }}>⚠️ Board render error</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 2, wordBreak: 'break-all' }}>at {path}</div>
            {isAdminSubpage ? <div style={{ fontSize: 13, color: '#FCD34D', marginTop: 6 }}>Returning to tournaments list…</div> : null}
            <div style={{ fontSize: 18, fontWeight: 800, color: '#FF9B9B', marginTop: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message}</div>
            <div style={lbl}>Component stack — which component threw</div>
            <pre style={{ ...pre, maxHeight: '38vh' }}>{compStack || '(none captured)'}</pre>
            <div style={lbl}>Error stack</div>
            <pre style={{ ...pre, maxHeight: '32vh', fontSize: 12 }}>{errStack || '(no stack)'}</pre>
            <div style={{ marginTop: 16 }}>
              <button onClick={() => window.location.reload()} style={{ padding: '10px 18px', borderRadius: 10, background: '#123D26', color: '#123D26', fontWeight: 900, border: 'none', cursor: 'pointer' }}>Reload</button>
              <a href="/admin" style={{ marginLeft: 14, color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>Admin</a>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

function Landing() {
  // Belt-and-suspenders: the public marketing landing does not exist on the
  // TEST 4Ball-only site — if anything ever routes here, bounce to the admin
  // access-code → dashboard flow. Club/live render the landing normally.
  if (PBCC_PREMIUM) return <Navigate to="/admin/pbcc-4ball" replace />
  const heroBg = CLUB.heroImage || CLUB.courseMapImage || ''
  const features = [
    { Icon: MapPin,     label: 'Live GPS Tracking' },
    { Icon: Smartphone, label: 'Mobile Scoring' },
    { Icon: Trophy,     label: 'Live Leaderboards' },
    { Icon: Users,      label: 'Tournament Pairings' },
  ]
  return (
    <div style={{ minHeight: '100vh' }}>
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <header className="relative w-full overflow-hidden">
        {/* Background image on its own layer so the filter (contrast +
            saturate + brightness) sharpens the aerial without touching
            the logo, headline, or cards. Slight scale hides filter-edge
            bleed at the crop. */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${heroBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'contrast(1.10) saturate(1.22) brightness(1.04)',
            transform: 'scale(1.02)',
            transformOrigin: 'center',
          }}
        />
        {/* Edge-frame + vertical fade + soft vignette stacked on top of
            the filtered photo — keeps the title legible without muddying
            the course detail. */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: [
              'radial-gradient(120% 80% at 50% 35%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.35) 100%)',
              'linear-gradient(to right, rgba(0,0,0,0.30), transparent 18%, transparent 82%, rgba(0,0,0,0.30))',
              'linear-gradient(to bottom, rgba(0,0,0,0.12), rgba(20,46,28,0.92))',
            ].join(', '),
          }}
        />

        <div className="absolute top-4 right-4 z-30">
          <button
            onClick={trialSignOut}
            onMouseEnter={(e) => { e.currentTarget.style.outlineColor = LAND_GOLD_ON_DARK; e.currentTarget.style.borderColor = LAND_GOLD_ON_DARK }}
            onMouseLeave={(e) => { e.currentTarget.style.outlineColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)' }}
            className="px-4 py-1.5 text-sm font-bold uppercase tracking-widest rounded-full transition"
            style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.4)',
              outline: '1.5px solid transparent',
              outlineOffset: '-2px',
              backdropFilter: 'blur(6px)',
              cursor: 'pointer',
            }}
          >
            Sign Out
          </button>
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 pt-6 pb-12">
          {CLUB.logo ? (
            <img
              src={CLUB.logoLight}
              alt={CLUB.name}
              className="mb-6"
              style={{ maxHeight: 280, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.45))' }}
            />
          ) : null}
          <h1
            className="text-white"
            style={{
              fontFamily: 'Cinzel, serif',
              fontSize: 'clamp(28px, 5vw, 52px)',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textShadow: '0 2px 10px rgba(0,0,0,0.65)',
              marginBottom: 16,
              lineHeight: 1,
            }}
          >
            {CLUB.name.split(' ').map((word, wi) => {
              const W = word.toUpperCase()
              return (
                <span key={wi}>
                  {wi > 0 ? ' ' : ''}
                  <span style={{ fontSize: '1.35em' }}>{W[0]}</span>
                  {W.slice(1)}
                </span>
              )
            })}
          </h1>
          {!FOURBALL_ONLY && (
          <div className="flex items-center justify-center gap-6">
            <span style={{ height: 1, width: 88, backgroundColor: LAND_GOLD_ON_DARK, display: 'inline-block' }} />
            <span style={{ color: LAND_GOLD_ON_DARK, fontSize: 26, fontWeight: 800, letterSpacing: '0.28em' }}>
              PRO SHOP OPERATIONS
            </span>
            <span style={{ height: 1, width: 88, backgroundColor: LAND_GOLD_ON_DARK, display: 'inline-block' }} />
          </div>
          )}
        </div>

        {/* Action cards — last child of hero with a small -mb so they
            sit just at the bottom of the photo. Feature bar adds enough
            top padding to leave a visible gap between card bottoms and
            the icon row (no clipping). */}
        <div className="px-4 sm:px-6 pb-4 relative z-20">
          {/* 4-col grid: the centerpiece spans 2 (wider) while the flanking
              cards keep 1 each. items-center keeps the side cards small and
              vertically centered against the taller middle card. */}
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
            {!FOURBALL_ONLY && <ActionCard to="/admin"   Icon={Shield} title="Admin Portal"       subtitle="Manage tournaments and settings" />}
            {/* Middle card: the centerpiece — wider (col-span-2), taller, larger
                serif. EventTitle typography/tiering, white text, same subtle
                border/fill as the other two. */}
            <Link
              to="/admin/pbcc-4ball"
              className={`md:col-span-2 ${FOURBALL_ONLY ? 'md:col-start-2' : ''} rounded-2xl px-6 py-9 flex items-center justify-center text-center transition-transform hover:-translate-y-0.5`}
              style={{
                minHeight: 168,
                backgroundColor: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                color: '#FFFFFF', boxShadow: '0 16px 36px rgba(0,0,0,0.28)',
                border: '2px solid rgba(255,255,255,0.55)', textDecoration: 'none',
              }}
            >
              <EventTitle
                info={{ name: '62nd Annual Pine Bluff Country Club\nFour-Ball Match Play Invitational', date: '2026-07-30', endDate: '2026-08-02' }}
                nameColor="#FFFFFF"
                dateColor="rgba(255,255,255,0.92)"
                nameSize={26}
                dateSize={16}
              />
            </Link>
            {!FOURBALL_ONLY && <ActionCard to="/history" Icon={Clock}  title="Tournament History" subtitle="Browse past tournaments and results" />}
          </div>
        </div>
      </header>

      {/* ── FEATURE BAR — solid dark green, top padding clears the cards ── */}
      <section
        className="px-4 sm:px-6"
        style={{
          backgroundColor: FOURBALL_ONLY ? '#FFFFFF' : LAND_BODY_GREEN,
          backgroundImage: FOURBALL_ONLY ? 'none' : 'linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0) 60%)',
          paddingTop: FOURBALL_ONLY ? 24 : 18,
          paddingBottom: FOURBALL_ONLY ? 24 : 6,
        }}
      >
        {FOURBALL_ONLY ? (
          // 4-Ball site: the 4 tabs as ONE tight cluster centered in the row.
          // inline-flex shrinks the container to the tabs' width (so there's
          // room to center it); a real 40px gap and no dividers/flex-1/stretch
          // keeps them from spreading edge-to-edge.
          <div className="w-full flex flex-col items-center gap-6">
            {/* three pine logos across the bar: left, middle, right */}
            <div className="w-full max-w-6xl mx-auto flex items-center justify-between gap-4">
              <img src="/pbcc-pine-green.png" alt="" style={{ height: 'clamp(220px, 26vw, 340px)', width: 'auto', objectFit: 'contain', display: 'block' }} />
              <img src="/pbcc-pine-green.png" alt={CLUB.name} style={{ height: 'clamp(220px, 26vw, 340px)', width: 'auto', objectFit: 'contain', display: 'block' }} />
              <img src="/pbcc-pine-green.png" alt="" style={{ height: 'clamp(220px, 26vw, 340px)', width: 'auto', objectFit: 'contain', display: 'block' }} />
            </div>
            <div className="inline-flex flex-wrap items-center justify-center" style={{ columnGap: 40, rowGap: 12 }}>
              {features.map((f) => (
                <FeatureItem key={f.label} Icon={f.Icon} label={f.label} textColor="#123D26" iconColor="#123D26" />
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-around gap-y-3">
            {features.flatMap((f, i) => {
              const item = <FeatureItem key={f.label} Icon={f.Icon} label={f.label} />
              if (i === 0) return [item]
              return [
                <span
                  key={`d${i}`}
                  aria-hidden
                  className="hidden sm:inline-block"
                  style={{ width: 1, height: 24, backgroundColor: 'rgba(255,255,255,0.25)' }}
                />,
                item,
              ]
            })}
          </div>
        )}
      </section>

      {/* ── BODY: cream panels flank course map on a green gradient ────── */}
      <section
        className="px-4 sm:px-6 pt-4 sm:pt-6 pb-6 sm:pb-8"
        style={{
          backgroundColor: LAND_BODY_GREEN,
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.18))`,
        }}
      >
        <div className="max-w-6xl mx-auto">
          {/* Desktop: 3-column grid, narrow side panels, wide center map */}
          <div
            className="hidden lg:grid gap-8 items-stretch"
            style={{
              gridTemplateColumns: 'minmax(260px, 1fr) minmax(0, 1.7fr) minmax(260px, 1fr)',
            }}
          >
            <RulesPanel title="Rules of Play" Icon={Flag} bullets={CLUB.rulesOfPlay || []} />
            {CLUB.courseMapImage ? (
              <div className="flex items-center justify-center">
                <img
                  src={CLUB.courseMapImage}
                  alt={`${CLUB.name} Course Map`}
                  className="w-full max-w-[560px]"
                  style={{ filter: 'drop-shadow(0 14px 22px rgba(0,0,0,0.45))' }}
                />
              </div>
            ) : <div />}
            <RulesPanel title="Club Rules" Icon={Home} bullets={CLUB.clubRules || []} />
          </div>

          {/* Mobile: map first, then panels stacked */}
          <div className="lg:hidden grid gap-8">
            {CLUB.courseMapImage ? (
              <div className="flex items-center justify-center">
                <img
                  src={CLUB.courseMapImage}
                  alt={`${CLUB.name} Course Map`}
                  className="w-full max-w-[560px]"
                  style={{ filter: 'drop-shadow(0 14px 22px rgba(0,0,0,0.45))' }}
                />
              </div>
            ) : null}
            <RulesPanel title="Rules of Play" Icon={Flag} bullets={CLUB.rulesOfPlay || []} />
            <RulesPanel title="Club Rules"    Icon={Home} bullets={CLUB.clubRules    || []} />
          </div>

          {CLUB.showPoweredBy ? (
            <p className="mt-8 text-center text-sm uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Powered by CompleteTourneyLive
            </p>
          ) : null}
        </div>
      </section>
    </div>
  )
}

// Hero-overlap CTA card. featured=true uses the gold gradient; otherwise
// a white card with a primary-green border. All colors derive from CLUB,
// so a rebrand in club.js propagates through every card automatically.
function ActionCard({ to, Icon, title, subtitle, featured = false }) {
  const cardStyle = featured
    ? {
        backgroundColor: 'rgba(255,255,255,0.10)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        color: '#FFFFFF',
        boxShadow: '0 14px 36px rgba(0,0,0,0.36)',
        border: `2px solid ${CLUB.accentColor}`,
      }
    : {
        backgroundColor: 'rgba(255,255,255,0.08)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        color: '#FFFFFF',
        boxShadow: '0 12px 28px rgba(0,0,0,0.20)',
        border: '2px solid rgba(255,255,255,0.55)',
      }
  const iconColor = featured ? CLUB.accentColor : '#FFFFFF'
  return (
    <Link
      to={to}
      className="rounded-2xl p-4 flex items-center gap-3 transition-transform hover:-translate-y-0.5"
      style={{ ...cardStyle, textDecoration: 'none' }}
    >
      <Icon size={28} strokeWidth={1.75} color={iconColor} />
      <div className="flex-1">
        <div className="font-extrabold text-base" style={{ letterSpacing: '0.02em' }}>{title}</div>
        <div className="text-sm" style={{ opacity: 0.85 }}>{subtitle}</div>
      </div>
      <ChevronRight
        size={20}
        strokeWidth={2}
        color={iconColor}
        style={{ opacity: featured ? 0.7 : 0.5 }}
      />
    </Link>
  )
}

// Feature-strip atom: gold icon + uppercase label, rendered on the dark
// translucent bar that sits between hero and body.
function FeatureItem({ Icon, label, textColor = '#FFFFFF', iconColor = LAND_GOLD_ON_DARK }) {
  return (
    <div className="flex items-center gap-3 px-4 py-1" style={{ color: textColor }}>
      <Icon size={28} strokeWidth={1.75} color={iconColor} />
      <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '0.18em' }}>
        {label.toUpperCase()}
      </span>
    </div>
  )
}

// Cream-panel helper used by Landing for "Rules of Play" and "Club Rules."
// Green circular badge (white icon), Cinzel title, gold Check bullets,
// and a gold "View Full Rules →" link pinned at the bottom so adjacent
// panels stay the same height in the desktop grid.
function RulesPanel({ title, Icon, bullets }) {
  return (
    <div
      className="rounded-xl p-4 flex flex-col"
      style={{
        backgroundColor: FOURBALL_ONLY ? '#FFFFFF' : '#EEE7D2',
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 10px 26px rgba(0,0,0,0.22)',
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="rounded-full flex items-center justify-center"
          style={{
            width: 40,
            height: 40,
            backgroundColor: CLUB.primaryColor,
            flexShrink: 0,
          }}
        >
          <Icon size={20} strokeWidth={1.75} color="#FFFFFF" />
        </div>
        <h2
          style={{
            fontFamily: 'Cinzel, serif',
            fontSize: 20,
            fontWeight: 600,
            color: CLUB.primaryColor,
            letterSpacing: '0.04em',
            margin: 0,
          }}
        >
          {title}
        </h2>
      </div>
      <ul className="space-y-1.5 flex-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm leading-snug" style={{ color: '#1F2937' }}>
            <Check size={16} strokeWidth={2.5} color={LAND_GOLD_ON_LIGHT} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <a
        href="#"
        className="mt-3 inline-flex items-center gap-1.5 self-start"
        style={{
          color: LAND_GOLD_ON_LIGHT,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          textDecoration: 'none',
          borderBottom: `1px solid ${LAND_GOLD_ON_LIGHT}`,
          paddingBottom: 1,
        }}
      >
        View Full Rules <span style={{ marginLeft: 2 }}>→</span>
      </a>
    </div>
  )
}
